import { useLocalization } from "@fluent/react";
import { type Control } from "react-hook-form";
import { TextInput } from "@lending/components/TextInput/TextInput";
import { AutoDetailsFormInput } from "@lending/hooks/queries/AutoLoanApplication/types";
import { type VinLookupStatus } from "./useVinLookup";
import styles from "./VinLookupPanel.module.css";
import { VinStatusIndicator } from "./VinStatusIndicator";

interface VinLookupPanelProps {
  control: Control<AutoDetailsFormInput>;
  /** Current VIN input length, for the in-input character counter. */
  vinLength: number;
  status: VinLookupStatus;
  /** Adds the design's spacing between the new/used toggle and the panel. */
  hasPrecedingToggle: boolean;
}

/**
 * The "Have your VIN?" panel: an optional VIN input that auto-decodes a
 * complete VIN, with its lookup status shown inside the input and a failed
 * lookup surfaced as an inline error.
 */
function VinLookupPanel({ control, vinLength, status, hasPrecedingToggle }: VinLookupPanelProps) {
  const { l10n } = useLocalization();

  return (
    <div className={`${styles.panel} ${hasPrecedingToggle ? styles.panelAfterToggle : ""}`}>
      <div className={styles.heading}>
        <span className={styles.title}>
          {l10n.getString("auto-loan-auto-details-vin-panel-title", null, "Have your VIN?")}
        </span>{" "}
        {l10n.getString(
          "auto-loan-auto-details-vin-panel-subtitle",
          null,
          "We'll prefill the rest."
        )}
      </div>
      <TextInput
        name="vin"
        control={control}
        label={l10n.getString("auto-loan-auto-details-vin-label", null, "VIN (optional)")}
        type="text"
        endContent={<VinStatusIndicator status={status} vinLength={vinLength} />}
        error={
          status === "noMatch"
            ? l10n.getString(
                "auto-loan-auto-details-vin-unmatched",
                null,
                "Unable to match that VIN."
              )
            : undefined
        }
        transform={(value) =>
          value
            .replace(/[^a-zA-Z0-9]/g, "")
            .slice(0, 17)
            .toUpperCase()
        }
      />
    </div>
  );
}

export { VinLookupPanel };
